/**
 * Dictionaries API — reference-data endpoints
 * Used to populate dropdowns on the New Standard form.
 */

/**
 * Fetch a single dictionary by name.
 * @param {string} apiBaseUrl
 * @param {Function} callProtectedApi
 * @param {string} name  e.g. "document-types"
 * @returns {Promise<{id:string, value:string, active:boolean}[]>}
 */
export const getDictionary = async (apiBaseUrl, callProtectedApi, name) => {
    const url = `${apiBaseUrl}/api/v1/dictionaries/${encodeURIComponent(name)}`;
    const res = await callProtectedApi(url);
    return res?.data?.items ?? [];
};

/**
 * Fetch multiple dictionaries in one request.
 * @param {string} apiBaseUrl
 * @param {Function} callProtectedApi
 * @param {string[]} names  e.g. ["document-types","document-statuses","tags"]
 * @returns {Promise<Record<string, {id:string, value:string, active:boolean}[]>>}
 */
export const getDictionariesBulk = async (apiBaseUrl, callProtectedApi, names) => {
    const csv = names.map(encodeURIComponent).join(",");
    const url = `${apiBaseUrl}/api/v1/dictionaries?names=${csv}`;
    const res = await callProtectedApi(url);
    return res?.data?.dictionaries ?? {};
};

/**
 * Fetch child PDSS levels for a given parent.
 * @param {string} apiBaseUrl
 * @param {Function} callProtectedApi
 * @param {string|number} parentId
 * @returns {Promise<{id:string, value:string, active:boolean}[]>}
 */
export const getPdssChildren = async (apiBaseUrl, callProtectedApi, parentId) => {
    const url = `${apiBaseUrl}/api/v1/dictionaries/pdss-levels/${encodeURIComponent(parentId)}/children`;
    const res = await callProtectedApi(url);
    return res?.data?.items ?? [];
};

export const getStandardOwners = async (
    apiBaseUrl,
    callProtectedApi,
    { search = "", page = 1, size = 20 } = {}
) => {
    const url =
        `${apiBaseUrl}/api/v1/dictionaries/standard-owners` +
        `?search=${encodeURIComponent(String(search ?? "").trim())}` +
        `&page=${encodeURIComponent(page)}` +
        `&size=${encodeURIComponent(size)}`;
    const res = await callProtectedApi(url);
    return {
        items: Array.isArray(res?.data?.items) ? res.data.items : [],
        totalCount: Number(res?.data?.totalCount) || 0,
        page: Number(res?.data?.page) || page,
        size: Number(res?.data?.size) || size,
        success: Boolean(res?.data?.success),
    };
};

export const getRegionalOwnerSuggestions = async (
    apiBaseUrl,
    callProtectedApi,
    { search = "", size = 8 } = {}
) => {
    const params = new URLSearchParams();
    const query = String(search ?? "").trim();
    const limit = Number(size) || 8;

    if (query) params.set("search", query);
    params.set("size", String(limit));

    const url =
        `${apiBaseUrl}/api/v1/dictionaries/functional-owner/suggestions` +
        `?${params.toString()}`;
    const res = await callProtectedApi(url);

    return {
        items: Array.isArray(res?.data?.items)
            ? res.data.items
                .map((item) => String(item ?? "").trim())
                .filter(Boolean)
            : [],
        success: Boolean(res?.data?.success),
    };
};

export const getStandardOwnerById = async (apiBaseUrl, callProtectedApi, id) => {
    if (!apiBaseUrl || id == null || id === "") return null;
    const url = `${apiBaseUrl}/api/v1/dictionaries/standard-owners/${encodeURIComponent(id)}`;
    const res = await callProtectedApi(url);
    if (Array.isArray(res?.data?.items)) return res.data.items[0] ?? null;
    if (res?.data?.item && typeof res.data.item === "object") return res.data.item;
    return res?.data && typeof res.data === "object" ? res.data : null;
};
